/* EVERWORD 1.8: cart-only enhancements. Shopify owns price, inventory and checkout. */
(() => {
  'use strict';
  const signature = item => JSON.stringify([String(item.variant_id), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), item.selling_plan_allocation?.selling_plan?.id || null]);
  const cleanTracking = properties => Object.fromEntries(Object.entries(properties || {}).filter(([key]) => !['_ew_upgrade_pending', '_ew_upgrade_source', '_ew_upgrade_id'].includes(key)));
  const propertySignature = properties => JSON.stringify(Object.entries(cleanTracking(properties)).sort(([a], [b]) => a.localeCompare(b)));
  const pending = cart => cart.items.some(item => item.properties?._ew_upgrade_pending === 'true');
  const message = text => new Error(text);
  function createClient(rootUrl, fetcher, makeId) {
    const base = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    async function request(path, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetcher(path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
        const result = await response.json();
        if (!response.ok) throw message(typeof result.description === 'string' ? result.description : 'Shopify could not update this cart. Reload it to review the latest items.');
        return result;
      } catch (error) {
        // Never retry a write automatically: the server may already have applied it.
        if (error.name === 'AbortError' || error instanceof TypeError || error instanceof SyntaxError) throw message('We couldn’t confirm the cart update. Reload and review your cart before trying again.');
        throw error;
      } finally { clearTimeout(timer); }
    }
    const read = () => request(`${base}cart.js`);
    const write = (action, body) => request(`${base}cart/${action}.js`, body);
    function ready(cart) { if (pending(cart)) throw message('Review the interrupted packaging change before making another change.'); }
    function sourceLine(cart, key) { const item = cart.items.find(line => line.key === key); if (!item) throw message('Your cart changed in another window. Reload to review the latest items.'); return item; }
    function cleanQuantity(cart, tracked) {
      const trackedProperties = propertySignature(tracked.properties);
      return cart.items
        .filter(item => String(item.variant_id) === String(tracked.variant_id) && !item.properties?._ew_upgrade_id && propertySignature(item.properties) === trackedProperties)
        .reduce((total, item) => total + item.quantity, 0);
    }
    async function finalizeTrackedLine(tracked, cart = null) {
      const token = tracked.properties?._ew_upgrade_id;
      if (!token) return cart || read();
      let current = cart || await read();
      const marked = current.items.find(item => item.properties?._ew_upgrade_id === token);
      if (!marked) return current;
      const quantityBefore = cleanQuantity(current, marked);
      await write('add', { items: [{ id: marked.variant_id, quantity: marked.quantity, properties: cleanTracking(marked.properties) }] });
      current = await read();
      if (cleanQuantity(current, marked) !== quantityBefore + marked.quantity) throw message('Reload and review your cart before continuing.');
      const freshMarked = current.items.find(item => item.properties?._ew_upgrade_id === token);
      if (!freshMarked) throw message('Reload and review your cart before continuing.');
      await write('change', { id: freshMarked.key, quantity: 0 });
      current = await read();
      if (current.items.some(item => item.properties?._ew_upgrade_id === token)) throw message('Reload and review your cart before continuing.');
      return current;
    }
    async function upgrade({ key, targetId, productUrl, displayedPrice, packagingOption = 'Packaging' }) {
      const before = await read(); ready(before);
      const source = sourceLine(before, key);
      if (source.selling_plan_allocation || source.parent_relationship || source.item_components?.length) throw message('Please update this item’s options on its product page.');
      const product = await request(`${productUrl.split('?')[0]}.js`);
      const variant = product.variants.find(value => String(value.id) === String(targetId));
      if (String(product.id) !== String(source.product_id) || !variant?.available || variant.price <= source.price) throw message('This upgrade is no longer available. Your original gift is still in your cart.');
      const current = product.variants.find(value => String(value.id) === String(source.variant_id));
      const packagingIndex = product.options?.findIndex(option => (typeof option === 'string' ? option : option.name) === packagingOption);
      if (!current || packagingIndex === undefined || packagingIndex < 0 || !current.options || !variant.options || current.options[packagingIndex] === variant.options[packagingIndex] || current.options.some((option, index) => index !== packagingIndex && option !== variant.options[index])) throw message('This mapping changes more than packaging. Your original gift is unchanged; please review its product options.');
      if (variant.price !== Number(displayedPrice)) throw message('The upgrade price changed. Reload your cart to review its current price.');
      const token = makeId();
      const properties = { ...source.properties, _ew_upgrade_pending: 'true', _ew_upgrade_source: source.key, _ew_upgrade_id: token };
      await write('add', { items: [{ id: variant.id, quantity: source.quantity, properties }] });
      // Add first, verify inventory/quantity, and only then remove the original.
      let cart = await read();
      const added = cart.items.find(item => item.properties?._ew_upgrade_id === token);
      const originals = cart.items.filter(item => signature(item) === signature(source));
      if (!added || added.quantity !== source.quantity || originals.length !== 1 || originals[0].quantity !== source.quantity) throw message('The packaging change needs a review. Your cart may contain both versions. Reload your cart and keep the version you want.');
      // Discounts can change line keys after the add, so use the freshly matched key.
      await write('change', { id: originals[0].key, quantity: 0 });
      cart = await read();
      if (cart.items.some(item => signature(item) === signature(source))) throw message('The original gift is still present. Reload and review both versions.');
      const confirmed = cart.items.find(item => item.properties?._ew_upgrade_id === token);
      if (!confirmed || confirmed.quantity !== source.quantity) throw message('Your cart changed during the upgrade. Reload to review it.');
      return finalizeTrackedLine(confirmed, cart);
    }
    async function add({ productId, variantId, productUrl }) {
      const cart = await read(); ready(cart);
      if (cart.items.some(item => String(item.product_id) === String(productId))) throw message('This gift is already in your cart. Reload to review its quantity.');
      const product = await request(`${productUrl.split('?')[0]}.js`);
      const variant = product.variants.find(item => String(item.id) === String(variantId));
      if (String(product.id) !== String(productId) || product.variants.length !== 1 || product.requires_selling_plan || !variant?.available) throw message('This gift’s options changed. Open its product page to choose your gift.');
      await write('add', { items: [{ id: variant.id, quantity: 1 }] });
      return read();
    }
    async function personalize(key, edits) {
      const cart = await read(); ready(cart); const item = sourceLine(cart, key);
      if (Object.keys(edits).some(name => name.startsWith('_') || ['__proto__', 'constructor', 'prototype'].includes(name))) throw message('This field can’t be edited here.');
      await write('change', { id: key, quantity: item.quantity, properties: { ...item.properties, ...edits } });
      return read();
    }
    async function recoverInterrupted() {
      let cart = await read();
      let recovered = false;
      const snapshots = cart.items.filter(item => item.properties?._ew_upgrade_pending === 'true');
      for (const snapshot of snapshots) {
        const token = snapshot.properties._ew_upgrade_id;
        const item = cart.items.find(line => line.properties?._ew_upgrade_id === token);
        if (!item) continue;
        const counterpart = cart.items.some(line => line.key !== item.key && String(line.product_id) === String(item.product_id) && String(line.variant_id) !== String(item.variant_id) && propertySignature(line.properties) === propertySignature(item.properties));
        if (counterpart) continue;
        cart = await finalizeTrackedLine(item, cart);
        recovered = true;
      }
      return { cart, recovered, unresolved: pending(cart) };
    }
    async function confirmReview() {
      // Explicit customer confirmation; never infer which of two versions to keep.
      let cart = await read();
      while (pending(cart)) {
        const item = cart.items.find(line => line.properties?._ew_upgrade_pending === 'true');
        cart = await finalizeTrackedLine(item, cart);
      }
      return cart;
    }
    return { read, upgrade, add, personalize, recoverInterrupted, confirmReview };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { createClient, signature, cleanTracking, pending };
  if (typeof document === 'undefined') return;
  function mount(root) {
    if (!root || root.dataset.enhanced) return;
    const client = createClient(root.dataset.rootUrl, window.fetch.bind(window), () => window.crypto.randomUUID());
    const errorBox = root.querySelector('[data-cart-error]');
    const status = root.querySelector('[data-cart-status]');
    const checkout = root.querySelector('[name="checkout"]');
    const cartForm = root.querySelector('[data-cart-form]');
    let busy = false, uncertain = false;
    const storageKey = 'everword-cart-dismissals-v1';
    let dismissed = [];
    try { const stored = JSON.parse(sessionStorage.getItem(storageKey) || '{}'); if (Date.now() - stored.at < 86400000 && Array.isArray(stored.ids)) dismissed = stored.ids.filter(id => typeof id === 'string'); } catch (_) {}
    if (!cartForm) { try { sessionStorage.removeItem(storageKey); } catch (_) {} }
    const applyDismissals = () => root.querySelectorAll('[data-offer]').forEach(offer => { offer.hidden = dismissed.includes(offer.dataset.offer); });
    const say = text => { status.hidden = false; status.textContent = text; };
    const fail = text => { errorBox.hidden = false; errorBox.querySelector('[data-cart-error-copy]').textContent = text; errorBox.querySelector('a').focus(); };
    const dirtyQuantity = () => [...root.querySelectorAll('[data-original-quantity]')].some(input => input.value !== input.dataset.originalQuantity);
    const dirtyEditor = except => [...root.querySelectorAll('[data-personalization]')].find(form => form !== except && [...form.querySelectorAll('[data-property]')].some(input => input.value !== input.defaultValue));
    function canStart(except) {
      if (busy || uncertain || window.EverwordCartBusy) return false;
      if (dirtyQuantity()) { fail('Apply your quantity changes with Update cart before changing your gift options.'); return false; }
      const form = dirtyEditor(except);
      if (form) { form.closest('details').open = true; say('Save or cancel your personalization changes first.'); form.querySelector('[data-property]').focus(); return false; }
      return true;
    }
    async function run(task) {
      if (busy || uncertain) return;
      busy = true; window.EverwordCartBusy = true; root.setAttribute('aria-busy', 'true');
      root.querySelectorAll('button,input,textarea').forEach(el => { el.dataset.ewWasDisabled = String(el.disabled); el.disabled = true; });
      say('Updating your gifts…');
      try { await task(); window.location.assign(root.dataset.cartUrl); }
      catch (error) {
        uncertain = true;
        root.querySelectorAll('button,input,textarea').forEach(el => { el.disabled = el.dataset.ewWasDisabled === 'true'; });
        if (checkout) checkout.disabled = true;
        fail(error.message || 'Reload and review your cart before trying again.');
        status.hidden = true;
      } finally { busy = false; window.EverwordCartBusy = false; root.removeAttribute('aria-busy'); }
    }
    root.addEventListener('submit', event => {
      const form = event.target;
      if (busy || uncertain || (window.EverwordCartBusy && !busy)) { event.preventDefault(); return; }
      if (form === cartForm) {
        const unsaved = dirtyEditor();
        if (unsaved) { event.preventDefault(); unsaved.closest('details').open = true; say('Save or cancel your personalization before continuing.'); unsaved.querySelector('[data-property]').focus(); }
        if (event.submitter?.name === 'checkout' && root.dataset.conflict === 'true') event.preventDefault();
        return;
      }
      if (form.matches('[data-cross-sell]')) {
        event.preventDefault(); if (!canStart()) return;
        run(() => client.add({ productId: form.dataset.productId, variantId: form.elements.id.value, productUrl: form.dataset.productUrl }));
      }
      if (form.matches('[data-personalization]')) {
        event.preventDefault(); if (!canStart(form)) return;
        const inputs = [...form.querySelectorAll('[data-property]')];
        for (const input of inputs) {
          input.setCustomValidity('');
          if (input.required && !input.value.trim()) input.setCustomValidity('Enter your personalization.');
          if ((input.minLength > 0 && input.value.length < input.minLength && input.value) || (input.maxLength > 0 && input.value.length > input.maxLength)) input.setCustomValidity('Check the length of your personalization.');
        }
        if (!form.reportValidity()) return;
        const edits = Object.fromEntries(inputs.map(input => [input.dataset.property, input.value.trim()]));
        run(() => client.personalize(form.dataset.key, edits));
      }
    });
    root.addEventListener('input', event => event.target.setCustomValidity?.(''));
    root.addEventListener('click', event => {
      if (busy) { event.preventDefault(); return; }
      const button = event.target.closest('button');
      if (!button) return;
      if (button.matches('[data-cancel-edit]')) { const form = button.closest('form'); form.reset(); form.querySelectorAll('[data-property]').forEach(input => input.setCustomValidity('')); form.closest('details').open = false; form.closest('details').querySelector('summary').focus(); }
      if (button.matches('[data-upgrade]') && canStart()) run(() => client.upgrade({ key: button.dataset.key, targetId: button.dataset.target, productUrl: button.dataset.productUrl, displayedPrice: button.dataset.targetPrice, packagingOption: button.dataset.packagingOption }));
      if (button.matches('[data-confirm-review]') && canStart()) run(() => client.confirmReview());
      if (button.matches('[data-dismiss]')) {
        const offer = button.closest('[data-offer]');
        if (!dismissed.includes(offer.dataset.offer)) dismissed.push(offer.dataset.offer);
        try { sessionStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), ids: dismissed.slice(-50) })); } catch (_) {}
        offer.hidden = true; say('Offer skipped. Your gifts are unchanged.'); checkout?.focus();
        // A checkout app must explicitly integrate this event/state; it does not receive it automatically.
        root.dispatchEvent(new CustomEvent('everword:offer-dismissed', { bubbles: true, detail: { offerId: offer.dataset.offer } }));
      }
    });
    root.dataset.enhanced = 'true'; applyDismissals();
    if (root.dataset.conflict === 'true') {
      say(root.dataset.recoveryStatus || 'Finishing your packaging update…');
      client.recoverInterrupted().then(result => {
        if (result.recovered) window.location.replace(root.dataset.cartUrl);
        else if (result.unresolved) {
          status.hidden = true;
          fail(root.dataset.recoveryError || 'A packaging change needs review. Remove the unwanted version, or confirm that you want to keep the items shown.');
        }
      }).catch(error => {
        status.hidden = true;
        fail(error.message || root.dataset.recoveryError || 'Reload and review your cart before continuing.');
      });
    }
  }
  const init = () => document.querySelectorAll('[data-ew-cart]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
